import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { formatDateTime } from '../../utils/Utils';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import AssignmentTargetPicker from '../../components/ui/AssignmentTargetPicker';
import ConfirmDialog from '../../components/ui/ConfirmDialog';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const Title = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text.primary};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 8px;
`;

const InfoItem = styled.div`
  font-family: 'Nunito Sans', sans-serif;
`;

const Label = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
`;

const Value = styled.div`
  font-size: 14px;
  color: ${(p) => p.theme.colors.text.primary};
  font-weight: 600;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  border-bottom: 2px solid #E0E4E8;
  font-weight: 600;
  color: #64748b;
  font-size: 12px;
`;

const Td = styled.td`
  padding: 10px 12px;
  border-bottom: 1px solid #E0E4E8;
  color: ${(p) => p.theme.colors.text.primary};
`;

const DrawerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const DrawerContent = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 28px;
  width: 520px;
  max-width: 90vw;
`;

const FormRow = styled.div`
  margin-bottom: 16px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  color: #ef4444;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
  &:hover { background: #fef2f2; }
`;

export default function ImportantLinkDetailsPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const { canWrite } = useAuth();

  const [link, setLink] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });
  const [deleting, setDeleting] = useState(false);

  const [targets, setTargets] = useState({
    userIds: [],
    departmentIds: [],
    locationIds: [],
    userGroupIds: [],
    subTenantIds: [],
    includeAll: false,
    excludes: {
      userIds: [],
      departmentIds: [],
      locationIds: [],
      userGroupIds: [],
      subTenantIds: [],
    },
  });

  useEffect(() => {
    api.get(`/v1/important-links/${id}`)
      .then((res) => setLink(res.data?.data));
  }, [id]);

  const fetchAssignments = useCallback(() => {
    setLoading(true);
    api.get(`/v1/important-links/${id}/assignments`)
      .then((res) => {
        const result = res.data?.data;
        setAssignments(Array.isArray(result) ? result : []);
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const assignmentTypeLabels = useMemo(() => ({
    0: t('assignment.includeAll'),
    1: t('importantLink.assignDepartment'),
    2: t('importantLink.assignLocation'),
    3: t('importantLink.assignUserGroup'),
    4: t('importantLink.assignIndividual'),
    5: t('assignment.targetSubTenant'),
  }), [t]);

  const handleOpenDrawer = useCallback(() => {
    setTargets({
      userIds: [], departmentIds: [], locationIds: [],
      userGroupIds: [], subTenantIds: [], includeAll: false,
    });
    setDrawerOpen(true);
  }, []);

  const hasAnyTarget = targets.includeAll
    || targets.userIds.length > 0
    || targets.departmentIds.length > 0
    || targets.locationIds.length > 0
    || targets.userGroupIds.length > 0
    || targets.subTenantIds.length > 0;

  const handleSave = useCallback(async () => {
    if (!hasAnyTarget) return;
    setSaving(true);
    try {
      await api.post('/v1/important-links/assignments', {
        importantLinkId: id,
        ...targets,
      });
      setDrawerOpen(false);
      fetchAssignments();
    } catch {
      // error handled by interceptor
    } finally {
      setSaving(false);
    }
  }, [id, targets, hasAnyTarget, fetchAssignments]);

  const handleDelete = useCallback(async () => {
    setDeleting(true);
    try {
      await api.delete(`/v1/important-links/assignments/${deleteConfirm.id}`);
      setDeleteConfirm({ isOpen: false, id: null });
      fetchAssignments();
    } catch {
      // error handled by interceptor
    } finally {
      setDeleting(false);
    }
  }, [deleteConfirm.id, fetchAssignments]);

  const linkTitle = link ? link.title : '...';

  return (
    <PageContainer>
      <Breadcrumbs items={[
        { label: t('importantLink.pageTitle'), path: '/important-links' },
        { label: linkTitle },
      ]} />

      {link && (
        <Card>
          <Title>{t('importantLink.details')}</Title>
          <InfoGrid>
            <InfoItem>
              <Label>{t('importantLink.title')}</Label>
              <Value>{link.title}</Value>
            </InfoItem>
            <InfoItem>
              <Label>{t('importantLink.url')}</Label>
              <Value>
                <a href={link.url} target="_blank" rel="noopener noreferrer" style={{ color: '#0291EF' }}>{link.url}</a>
              </Value>
            </InfoItem>
            {link.description && (
              <InfoItem>
                <Label>{t('importantLink.description')}</Label>
                <Value>{link.description}</Value>
              </InfoItem>
            )}
          </InfoGrid>
        </Card>
      )}

      <Card>
        <Header>
          <Title>{t('importantLink.assignments')}</Title>
          {canWrite('IL') && (
            <Button onClick={handleOpenDrawer}>
              {t('importantLink.newAssignment')}
            </Button>
          )}
        </Header>

        <Table>
          <thead>
            <tr>
              <Th>{t('importantLink.assignmentType')}</Th>
              <Th>{t('importantLink.targetName')}</Th>
              <Th>{t('importantLink.createDate')}</Th>
              {canWrite('IL') && <Th />}
            </tr>
          </thead>
          <tbody>
            {assignments.length === 0 && !loading && (
              <tr><Td colSpan={4} style={{ textAlign: 'center', color: '#A0A8B4' }}>{t('common.noRecords')}</Td></tr>
            )}
            {assignments.map((a) => (
              <tr key={a.id}>
                <Td>{assignmentTypeLabels[a.assignmentType] || a.assignmentType}</Td>
                <Td>{a.targetName || '-'}</Td>
                <Td>{formatDateTime(a.createDate, i18n.language)}</Td>
                {canWrite('IL') && (
                  <Td>
                    <IconButton onClick={() => setDeleteConfirm({ isOpen: true, id: a.id })}>
                      {t('common.delete')}
                    </IconButton>
                  </Td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('importantLink.deleteAssignmentTitle')}
        message={t('importantLink.deleteAssignmentMessage')}
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null })}
        loading={deleting}
      />

      {drawerOpen && (
        <DrawerOverlay onClick={() => !saving && setDrawerOpen(false)}>
          <DrawerContent onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontFamily: "'Nunito Sans', sans-serif", fontSize: 16, fontWeight: 700, marginBottom: 20, color: '#004242' }}>
              {t('importantLink.newAssignment')}
            </h3>

            <AssignmentTargetPicker value={targets} onChange={setTargets} />

            <Footer>
              <Button variant="ghost" onClick={() => setDrawerOpen(false)} disabled={saving}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSave} disabled={!hasAnyTarget || saving}>
                {saving ? '...' : t('common.save')}
              </Button>
            </Footer>
          </DrawerContent>
        </DrawerOverlay>
      )}
    </PageContainer>
  );
}
